/**
 * 客户端上下文 <-> 后端 wire 契约的**边界映射**。
 *
 * 约束来源：CONTRACT.md 第 30/31 行（`PageContext = internal/contextx.Context JSON 字段`）
 * 与 etax-new/support/internal/contextx/context.go 的 Snapshot 校验：
 * - `protocolVersion` 必须精确为 `"1"`，缺失/其它值一律 1901003；
 * - 页面稳定标识字段名是 `pageId`（客户端/SDK 侧叫 `pageKey`），标题字段名是 `title`（`pageTitle`）；
 *   `pageInstanceId`/`pageId` 必须匹配 Go 的 `stableID`（标识而非 URL/查询串/凭据）；
 * - `period` 必须是 YYYYMM（`^[12][0-9]{3}(0[1-9]|1[0-2])$`，年份 1000..2999），SDK 侧是 YYYY-MM；
 * - `version` 必须是**非零**规范十进制字符串且不超过 uint64（Go 用 `ParseUint(..., 64)`）；
 * - `filters` 只允许后端 `approvedFilters` 白名单键（period/status/keyword/type/module），
 *   且凭据样式键（token/password/secret/cookie/authorization）一律在出站前剔除——
 *   绝不依赖后端收到后再过滤；
 * - 浏览器不可自证核实：`clientReported`/`companyVerified` 不是契约字段，出站一律不带；
 *   核实结论只能由服务端返回（`companyVerification`，权威）。
 *
 * 设计原则：
 * - 只做字段改名/形状转换，**绝不改语义**：不采集时间、不猜测账期数字、不放宽标识；
 * - 明确区分"未核实"（null/缺字段 → 省略）与"非法输入"（**报协议错误**，绝不静默省略）：
 *   非法账期/版本/标识不能让调用方以为只是"没填"；
 * - 反向解析只接受契约形状：缺必要字段或格式非法一律协议失败，
 *   绝不产出 pageInstanceId/version/pageKey 为空串的半成品，也绝不用请求方原始上下文回填响应。
 */

import { isCanonicalDecimal } from '@support/platform';

import { SupportError } from './errors';
import type { PageContext } from './types';

/** 后端上下文协议版本：contextx.Snapshot 只接受这一个值。 */
export const WIRE_CONTEXT_PROTOCOL_VERSION = '1';

/** 服务端权威核实结论值。 */
export const WIRE_COMPANY_VERIFICATION_IN_SCOPE = 'in_scope';
export const WIRE_COMPANY_VERIFICATION_UNVERIFIED = 'unverified';

/**
 * 后端 approvedFilters（internal/domain/message.go）白名单：只有这些业务筛选键会进入快照，
 * 其余键（含任何凭据样式键）在出站前就必须剔除。
 */
export const WIRE_APPROVED_FILTER_KEYS: readonly string[] = [
  'keyword',
  'module',
  'period',
  'status',
  'type',
];

/** 后端 wire 形状的页面上下文（internal/contextx.Context 的 JSON 字段）。 */
export interface WirePageContext {
  collectedAt?: string;
  companyId?: string;
  filters?: Record<string, string>;
  frontendVersion?: string;
  module?: string;
  pageId?: string;
  pageInstanceId: string;
  period?: string;
  product?: string;
  protocolVersion: string;
  routeName?: string;
  title?: string;
  version: string;
}

/** SDK/客户端侧账期形状：YYYY-MM（年 1000..2999，月 01..12）。 */
const DASHED_PERIOD = /^([12][0-9]{3})-(0[1-9]|1[0-2])$/u;
/** 后端规范账期形状：YYYYMM（同一套年份/月份约束，与 Go periodID 完全一致）。 */
const COMPACT_PERIOD = /^([12][0-9]{3})(0[1-9]|1[0-2])$/u;

/** 页面稳定标识（与 Go 的 stableID 同口径）：绝不放行 URL/查询串/凭据样式。 */
const STABLE_ID = /^[a-zA-Z0-9_.:-]{1,100}$/u;

/** uint64 上限（Go 用 ParseUint(...,64)，越界必被 1901003 拒绝）。 */
const MAX_UINT64 = 18_446_744_073_709_551_615n;
const MAX_UINT64_DIGITS = '18446744073709551615'.length;

/** 凭据样式键名：即使将来误加进白名单也必须剔除（与 Go 保留字策略一致）。 */
const CREDENTIAL_KEY_PATTERN = /(token|password|secret|cookie|authorization)/u;

function protocolError(message: string): SupportError {
  return new SupportError({ code: 0, kind: 'protocol', message });
}

function asRecord(value: unknown): null | Record<string, unknown> {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  return value as Record<string, unknown>;
}

function asNonEmptyString(value: unknown): null | string {
  return typeof value === 'string' && value !== '' ? value : null;
}

/** 非零规范十进制且不超过 uint64。 */
function isCanonicalUint64(value: unknown): value is string {
  if (typeof value !== 'string' || value.length > MAX_UINT64_DIGITS) return false;
  if (!isCanonicalDecimal(value)) return false;
  return BigInt(value) <= MAX_UINT64;
}

function isStableId(value: unknown): value is string {
  return typeof value === 'string' && STABLE_ID.test(value);
}

/** 出站筛选键：必须在后端白名单内，且不是凭据样式键。 */
export function isWireApprovedFilterKey(key: string): boolean {
  return WIRE_APPROVED_FILTER_KEYS.includes(key) && !CREDENTIAL_KEY_PATTERN.test(key.toLowerCase());
}

/** 出站筛选条件：只保留白名单键 + 非空字符串值；结果为空则该字段整体省略。 */
function toWireFilters(value: unknown): Record<string, string> | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  const out: Record<string, string> = {};
  for (const [key, entry] of Object.entries(record)) {
    if (!isWireApprovedFilterKey(key)) continue;
    const text = asNonEmptyString(entry);
    if (text === null) continue;
    out[key] = text;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/** 入站筛选条件：服务端已清洗过，客户端仍然只保留白名单键（凭据永不向上暴露）。 */
function fromWireFilters(value: unknown): Record<string, string> | undefined {
  if (value === undefined || value === null) return undefined;
  const record = asRecord(value);
  if (!record) throw protocolError('服务端筛选条件必须是对象');
  const out: Record<string, string> = {};
  for (const [key, entry] of Object.entries(record)) {
    if (typeof entry !== 'string') {
      // 非法键名本身可能就是秘密（例如误塞进来的 token 键）：不拼接键名
      throw protocolError('服务端筛选条件值必须是字符串');
    }
    if (!isWireApprovedFilterKey(key) || entry === '') continue;
    out[key] = entry;
  }
  return Object.keys(out).length === 0 ? undefined : out;
}

/**
 * 出站账期：SDK 的 YYYY-MM 显式转成后端 YYYYMM；已是 YYYYMM 的原样通过；
 * 未提供（null/undefined/空串）按"未核实"省略；**其余一律报协议错误**——
 * 非法账期不能被当成"没填"（那会让调用方以为宿主的数据是干净的）。
 */
export function toWirePeriod(period: null | string | undefined): string | undefined {
  if (period === null || period === undefined || period === '') return undefined;
  const match = DASHED_PERIOD.exec(period);
  if (match) return `${match[1]}${match[2]}`;
  if (COMPACT_PERIOD.test(period)) return period;
  // 错误文本绝不拼接输入值：账期字段可能是接入方误传的任意文本（甚至凭据）
  throw protocolError('账期不符合契约（YYYY-MM / YYYYMM，年份 1000..2999）');
}

/**
 * 入站账期：后端规范 YYYYMM 对称还原成 SDK 的 YYYY-MM；空值还原为显式 null（未核实）；
 * 非规范值按协议失败，绝不原样保留（原样保留会让"编码后静默丢失"发生在下一次上报里）。
 */
export function fromWirePeriod(period: unknown): null | string {
  const value = asNonEmptyString(period);
  if (value === null) {
    if (period === undefined || period === null || period === '') return null;
    throw protocolError('服务端账期字段类型非法');
  }
  if (COMPACT_PERIOD.test(value)) return `${value.slice(0, 4)}-${value.slice(4)}`;
  if (DASHED_PERIOD.test(value)) return value;
  throw protocolError('服务端账期不符合契约（YYYYMM / YYYY-MM）');
}

/** 可选文本字段：缺字段/空值省略；非法类型按协议失败。 */
function optionalText(record: Record<string, unknown>, key: string, label: string): string | undefined {
  const value = record[key];
  if (value === undefined || value === null || value === '') return undefined;
  if (typeof value !== 'string') {
    throw protocolError(`服务端上下文 ${label} 必须是字符串`);
  }
  return value;
}

/** 可空文本字段：缺字段/空值/非法类型（非字符串）语义明确，空值还原为显式 null。 */
function nullableText(value: unknown, label: string): null | string {
  if (value === undefined || value === null || value === '') return null;
  if (typeof value !== 'string') {
    throw protocolError(`服务端上下文 ${label} 必须是字符串`);
  }
  return value;
}

/**
 * 把客户端/SDK 形状的上下文编码为后端 wire 契约。
 * 版本非法/越界、页面标识不稳定、账期非法时**本地拒绝**
 * （后端会判 1901003，不发出注定失败的请求）。
 */
export function encodeWireContext(context: PageContext): WirePageContext {
  // 后端要求非零规范十进制且 <= uint64；'0'、带前导零、越界形式都会 1901003。
  if (!isCanonicalUint64(context.version) || context.version === '0') {
    // 只报固定字段名与错误类别：版本字段可能是任意文本（绝不拼接原值）
    throw protocolError('页面上下文版本必须是非零规范十进制字符串（<= uint64）');
  }
  const pageInstanceId = context.pageInstanceId;
  if (typeof pageInstanceId !== 'string' || !isStableId(pageInstanceId)) {
    throw protocolError('页面实例标识必须是稳定标识（字母/数字/_.:-，<= 100）');
  }
  const wire: WirePageContext = {
    protocolVersion: WIRE_CONTEXT_PROTOCOL_VERSION,
    pageInstanceId,
    version: context.version,
    collectedAt: context.collectedAt,
  };
  const pageKey = context.pageKey;
  if (!isStableId(pageKey)) {
    throw protocolError('页面稳定标识不能为空且必须是稳定标识（字母/数字/_.:-，<= 100）');
  }
  wire.pageId = pageKey;
  const pageTitle = asNonEmptyString(context.pageTitle);
  if (pageTitle !== null) wire.title = pageTitle;
  const module = asNonEmptyString(context.module);
  if (module !== null) wire.module = module;
  // 非空 routeName 必须与 Go stableID 同口径（contextx/context.go:48-50）：
  // URL/查询串/空白会被后端判「页面路由标识错误」，本地出站前就拒绝；
  // undefined/null/空串仍是「未提供」——省略字段，绝不改写字符串蒙混过校验。
  const routeName = context.routeName;
  if (routeName !== undefined && routeName !== null && routeName !== '') {
    if (!isStableId(routeName)) {
      throw protocolError('页面路由标识必须是稳定标识（字母/数字/_.:-，<= 100）');
    }
    wire.routeName = routeName;
  }
  const product = asNonEmptyString(context.product);
  if (product !== null) wire.product = product;
  const frontendVersion = asNonEmptyString(context.frontendVersion);
  if (frontendVersion !== null) wire.frontendVersion = frontendVersion;
  // 未核实的账套显式为空：不发字段，绝不沿用旧账套
  const companyId = asNonEmptyString(context.companyId);
  if (companyId !== null) wire.companyId = companyId;
  const period = toWirePeriod(context.period ?? null);
  if (period !== undefined) wire.period = period;
  const filters = toWireFilters(context.filters);
  if (filters !== undefined) wire.filters = filters;
  return wire;
}

/**
 * 把后端 wire 响应还原为客户端形状。
 *
 * - 非对象（含 null / 数组 / 基本类型）返回 undefined：调用方据此判定"该消息没有快照"；
 * - **对象但缺必要字段或格式非法**（protocolVersion 不是 "1"、pageInstanceId/pageId 非稳定标识、
 *   version 非非零规范十进制或越界、账期非法）一律抛协议错误：
 *   绝不产出空串半成品，也绝不让上层误以为拿到了服务端权威快照。
 * - `companyVerified` 只来自服务端权威字段 `companyVerification === 'in_scope'`。
 */
export function decodeWireContext(value: unknown): PageContext | undefined {
  const record = asRecord(value);
  if (!record) return undefined;
  if (record.protocolVersion !== WIRE_CONTEXT_PROTOCOL_VERSION) {
    // 只报固定字段名与期望常量，绝不回显服务端给的原值（可能是任意文本）
    throw protocolError(`服务端协议版本必须精确为 ${WIRE_CONTEXT_PROTOCOL_VERSION}`);
  }
  const pageInstanceId = record.pageInstanceId;
  if (!isStableId(pageInstanceId)) {
    throw protocolError('服务端 pageInstanceId 必须是稳定标识');
  }
  const pageId = record.pageId;
  if (!isStableId(pageId)) {
    // 旧数据 pageId="" 也走这里：Go 允许空 PageID，但客户端解不出 pageKey 的上下文
    // 一律按协议失败（宁可整条读取失败，也不产出一个假的半成品 pageKey）
    throw protocolError('服务端 pageId 必须是稳定标识');
  }
  const version = record.version;
  if (!isCanonicalUint64(version) || version === '0') {
    throw protocolError('服务端 version 必须是非零规范十进制字符串（<= uint64）');
  }
  const collectedAt = record.collectedAt;
  if (typeof collectedAt !== 'string') {
    throw protocolError('服务端 collectedAt 必须是字符串');
  }
  const companyVerification = asNonEmptyString(record.companyVerification) ?? undefined;
  const decoded: PageContext = {
    collectedAt,
    // 服务端清空账套 = 未核实：还原为显式 null，而不是空串
    companyId: nullableText(record.companyId, 'companyId'),
    companyVerified: companyVerification === WIRE_COMPANY_VERIFICATION_IN_SCOPE,
    pageInstanceId,
    pageKey: pageId,
    period: fromWirePeriod(record.period),
    receivedAt: nullableText(record.receivedAt, 'receivedAt'),
    version,
  };
  if (companyVerification !== undefined) decoded.companyVerification = companyVerification;
  // 企业名称只在服务端核实企业后给出，且只作展示。
  const companyName = asNonEmptyString(record.companyName);
  if (companyName !== null && decoded.companyVerified) decoded.companyName = companyName;
  // 服务端标记来源为客户端上报时才保留该标记，缺字段不冒充
  if (record.source !== undefined && record.source !== null && record.source !== '') {
    if (typeof record.source !== 'string') {
      throw protocolError('服务端 source 必须是字符串');
    }
    if (record.source === 'client_reported') decoded.clientReported = true;
  }
  const title = optionalText(record, 'title', 'title');
  if (title !== undefined) decoded.pageTitle = title;
  const module = optionalText(record, 'module', 'module');
  if (module !== undefined) decoded.module = module;
  // 入站 routeName 与出站同口径：非空必须是稳定标识（服务端存的也不会是 URL/查询串）
  const routeName = record.routeName;
  if (routeName !== undefined && routeName !== null && routeName !== '') {
    if (!isStableId(routeName)) {
      throw protocolError('服务端 routeName 必须是稳定标识（字母/数字/_.:-，<= 100）');
    }
    decoded.routeName = routeName;
  }
  const product = optionalText(record, 'product', 'product');
  if (product !== undefined) decoded.product = product;
  const frontendVersion = optionalText(record, 'frontendVersion', 'frontendVersion');
  if (frontendVersion !== undefined) decoded.frontendVersion = frontendVersion;
  const filters = fromWireFilters(record.filters);
  if (filters !== undefined) decoded.filters = filters;
  return decoded;
}